import type { ConversationTarget } from "@/lib/conversation-target";
import type { SessionMessageResponse } from "@/lib/session";

export interface NavigationCapabilities {
  schema_version: 1;
  turn_index: boolean;
  heading_contract_version: 1;
  target_window: boolean;
  child_navigation_metadata: boolean;
}

export interface NavigationOversizedMessage {
  kind: "oversized";
  message_id: string;
  session_id: string;
  run_id: string;
  role: string;
  status: string;
  created_at: string;
  metadata: Record<string, unknown>;
  revision: string;
  content_bytes: number;
  content_cursor: string;
  navigation_turn_id?: string | null;
}

export interface NavigationWindowResponse {
  schema_version: 1;
  target: { message_id: string; turn_id: string; thread_id: string | null };
  messages: Array<SessionMessageResponse | NavigationOversizedMessage>;
  coverage: {
    first_message_id: string;
    last_message_id: string;
    has_older: boolean;
    has_newer: boolean;
    before_cursor: string | null;
    after_cursor: string | null;
    starts_at_turn_boundary: boolean;
    ends_at_turn_boundary: boolean;
    partial_turn: boolean;
  };
  generation: string | null;
}

export interface NavigationContentResponse {
  schema_version: 1;
  message_id: string;
  revision: string;
  offset: number;
  text: string;
  next_cursor: string | null;
  complete: boolean;
}

export interface NavigationHeading {
  message_id: string;
  key: string;
  level: number;
  ordinal: number;
  text: string;
  content_fingerprint: string;
}

export interface NavigationTurn {
  turn_id: string;
  start_message_id: string;
  started_at: string;
  ordinal: number | null;
  pending: boolean;
  headings: NavigationHeading[];
  headings_next_cursor: string | null;
}

export interface SessionNavigationResponse {
  schema_version: 1;
  generation: string | null;
  state: "missing" | "building" | "partial" | "ready" | "error";
  coverage: {
    kind: "full" | "suffix";
    from_message_id: string | null;
    through_message_id: string | null;
  } | null;
  items: NavigationTurn[];
  next_cursor: string | null;
  previous_cursor: string | null;
}

export interface NavigationHeadingListResponse {
  schema_version: 1;
  generation: string | null;
  items: NavigationHeading[];
  next_cursor: string | null;
}

export type CapabilityProbeResult =
  | { state: "supported"; capabilities: NavigationCapabilities }
  | { state: "unsupported" }
  | { state: "denied"; status: number }
  | { state: "error"; status?: number };

type FetchWithAuth = (url: string, init: RequestInit) => Promise<Response>;
const NAVIGATION_REQUEST_TIMEOUT_MS = 15_000;

async function fetchNavigation(
  fetchWithAuth: FetchWithAuth,
  url: string,
  signal: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  const abort = () => controller.abort(signal.reason);
  signal.addEventListener("abort", abort, { once: true });
  if (signal.aborted) abort();
  const timeout = globalThis.setTimeout(
    () => controller.abort(new DOMException("Navigation request timed out", "TimeoutError")),
    NAVIGATION_REQUEST_TIMEOUT_MS,
  );
  try {
    return await fetchWithAuth(url, { headers: {}, signal: controller.signal });
  } finally {
    globalThis.clearTimeout(timeout);
    signal.removeEventListener("abort", abort);
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isJson(response: Response): boolean {
  return response.headers.get("content-type")?.toLowerCase().includes("application/json") === true;
}

function isCapabilities(value: unknown): value is NavigationCapabilities {
  return isObject(value)
    && value.schema_version === 1
    && value.heading_contract_version === 1
    && typeof value.turn_index === "boolean"
    && typeof value.target_window === "boolean"
    && typeof value.child_navigation_metadata === "boolean";
}

export function isOversizedNavigationMessage(
  value: SessionMessageResponse | NavigationOversizedMessage,
): value is NavigationOversizedMessage {
  return "kind" in value && value.kind === "oversized";
}

function isWindow(value: unknown): value is NavigationWindowResponse {
  if (!isObject(value) || value.schema_version !== 1 || !Array.isArray(value.messages)) return false;
  if (!isObject(value.target) || !isObject(value.coverage)) return false;
  const validMessage = (message: unknown) => {
    if (!isObject(message)) return false;
    if (message.kind === "oversized") {
      return typeof message.message_id === "string"
        && typeof message.session_id === "string"
        && typeof message.run_id === "string"
        && typeof message.role === "string"
        && typeof message.status === "string"
        && typeof message.created_at === "string"
        && isObject(message.metadata)
        && typeof message.revision === "string"
        && typeof message.content_bytes === "number"
        && typeof message.content_cursor === "string";
    }
    return typeof message.id === "string"
      && typeof message.session_id === "string"
      && typeof message.run_id === "string"
      && typeof message.role === "string"
      && typeof message.content === "string"
      && typeof message.status === "string"
      && typeof message.created_at === "string"
      && (message.metadata === undefined || isObject(message.metadata));
  };
  return typeof value.target.message_id === "string"
    && typeof value.target.turn_id === "string"
    && (value.target.thread_id === null || typeof value.target.thread_id === "string")
    && typeof value.coverage.first_message_id === "string"
    && typeof value.coverage.last_message_id === "string"
    && typeof value.coverage.has_older === "boolean"
    && typeof value.coverage.has_newer === "boolean"
    && (value.coverage.before_cursor === null || typeof value.coverage.before_cursor === "string")
    && (value.coverage.after_cursor === null || typeof value.coverage.after_cursor === "string")
    && typeof value.coverage.starts_at_turn_boundary === "boolean"
    && typeof value.coverage.ends_at_turn_boundary === "boolean"
    && typeof value.coverage.partial_turn === "boolean"
    && (value.generation === null || typeof value.generation === "string")
    && value.messages.every(validMessage);
}

function isContent(value: unknown): value is NavigationContentResponse {
  return isObject(value)
    && value.schema_version === 1
    && typeof value.message_id === "string"
    && typeof value.revision === "string"
    && typeof value.offset === "number"
    && typeof value.text === "string"
    && (value.next_cursor === null || typeof value.next_cursor === "string")
    && typeof value.complete === "boolean";
}

function isHeading(value: unknown): value is NavigationHeading {
  return isObject(value)
    && typeof value.message_id === "string"
    && typeof value.key === "string"
    && typeof value.level === "number"
    && typeof value.ordinal === "number"
    && typeof value.text === "string"
    && typeof value.content_fingerprint === "string";
}

function isNavigation(value: unknown): value is SessionNavigationResponse {
  if (!isObject(value) || value.schema_version !== 1 || !Array.isArray(value.items)) return false;
  if (!["missing", "building", "partial", "ready", "error"].includes(String(value.state))) return false;
  const coverageValid = value.coverage === null || (
    isObject(value.coverage)
    && (value.coverage.kind === "full" || value.coverage.kind === "suffix")
    && (value.coverage.from_message_id === null || typeof value.coverage.from_message_id === "string")
    && (value.coverage.through_message_id === null || typeof value.coverage.through_message_id === "string")
  );
  return (value.generation === null || typeof value.generation === "string")
    && coverageValid
    && (value.next_cursor === null || typeof value.next_cursor === "string")
    && (value.previous_cursor === null || value.previous_cursor === undefined || typeof value.previous_cursor === "string")
    && value.items.every((item) => isObject(item)
      && typeof item.turn_id === "string"
      && typeof item.start_message_id === "string"
      && typeof item.started_at === "string"
      && (item.ordinal === null || typeof item.ordinal === "number")
      && typeof item.pending === "boolean"
      && Array.isArray(item.headings)
      && item.headings.every(isHeading)
      && (item.headings_next_cursor === null || typeof item.headings_next_cursor === "string"));
}

function isHeadingList(value: unknown): value is NavigationHeadingListResponse {
  return isObject(value)
    && value.schema_version === 1
    && (value.generation === null || typeof value.generation === "string")
    && Array.isArray(value.items)
    && value.items.every(isHeading)
    && (value.next_cursor === null || typeof value.next_cursor === "string");
}

export async function probeNavigationCapabilities(
  fetchWithAuth: FetchWithAuth,
  sessionId: string,
  signal: AbortSignal,
): Promise<CapabilityProbeResult> {
  let response: Response;
  try {
    response = await fetchNavigation(
      fetchWithAuth,
      `/api/sessions/${encodeURIComponent(sessionId)}/navigation/capabilities`,
      signal,
    );
  } catch {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    return { state: "error" };
  }
  if ([404, 405, 501].includes(response.status)) return { state: "unsupported" };
  if (response.status === 401 || response.status === 403) {
    return { state: "denied", status: response.status };
  }
  if (!response.ok) return { state: "error", status: response.status };
  if (!isJson(response)) return { state: "unsupported" };
  const payload: unknown = await response.json().catch(() => null);
  return isCapabilities(payload)
    ? { state: "supported", capabilities: payload }
    : { state: "unsupported" };
}

function selectorForTarget(target: ConversationTarget): string {
  if (target.kind === "message") return `message_id=${encodeURIComponent(target.messageId)}`;
  if (target.kind === "turn") return `turn_id=${encodeURIComponent(target.turnId)}`;
  return `thread_id=${encodeURIComponent(target.threadId)}`;
}

export async function fetchNavigationWindow(
  fetchWithAuth: FetchWithAuth,
  sessionId: string,
  target: ConversationTarget,
  signal: AbortSignal,
): Promise<NavigationWindowResponse> {
  const response = await fetchNavigation(
    fetchWithAuth,
    `/api/sessions/${encodeURIComponent(sessionId)}/navigation/window?${selectorForTarget(target)}&limit=100`,
    signal,
  );
  if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status });
  if (!isJson(response)) throw new Error("navigation_window_invalid_content_type");
  const payload: unknown = await response.json().catch(() => null);
  if (!isWindow(payload)) throw new Error("navigation_window_invalid_response");
  return payload;
}

export async function fetchNavigationWindowPage(
  fetchWithAuth: FetchWithAuth,
  sessionId: string,
  direction: "before" | "after",
  cursor: string,
  signal: AbortSignal,
): Promise<NavigationWindowResponse> {
  const response = await fetchNavigation(
    fetchWithAuth,
    `/api/sessions/${encodeURIComponent(sessionId)}/navigation/window?${direction}=${encodeURIComponent(cursor)}&limit=100`,
    signal,
  );
  if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status });
  if (!isJson(response)) throw new Error("navigation_window_invalid_content_type");
  const payload: unknown = await response.json().catch(() => null);
  if (!isWindow(payload)) throw new Error("navigation_window_invalid_response");
  return payload;
}

export async function fetchNavigationContentChunk(
  fetchWithAuth: FetchWithAuth,
  sessionId: string,
  messageId: string,
  cursor: string,
  signal: AbortSignal,
): Promise<NavigationContentResponse> {
  const response = await fetchNavigation(
    fetchWithAuth,
    `/api/sessions/${encodeURIComponent(sessionId)}/navigation/messages/${encodeURIComponent(messageId)}/content?cursor=${encodeURIComponent(cursor)}&limit_bytes=262144`,
    signal,
  );
  if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status });
  if (!isJson(response)) throw new Error("navigation_content_invalid_content_type");
  const payload: unknown = await response.json().catch(() => null);
  if (!isContent(payload)) throw new Error("navigation_content_invalid_response");
  return payload;
}

export async function fetchSessionNavigation(
  fetchWithAuth: FetchWithAuth,
  sessionId: string,
  signal: AbortSignal,
  before?: string,
): Promise<SessionNavigationResponse> {
  const params = new URLSearchParams({ limit: "50" });
  if (before) params.set("before", before);
  const response = await fetchNavigation(
    fetchWithAuth,
    `/api/sessions/${encodeURIComponent(sessionId)}/navigation?${params}`,
    signal,
  );
  if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status });
  if (!isJson(response)) throw new Error("session_navigation_invalid_content_type");
  const payload: unknown = await response.json().catch(() => null);
  if (!isNavigation(payload)) throw new Error("session_navigation_invalid_response");
  return { ...payload, previous_cursor: payload.previous_cursor ?? null };
}

export async function fetchTurnHeadings(
  fetchWithAuth: FetchWithAuth,
  sessionId: string,
  turnId: string,
  signal: AbortSignal,
  after?: string,
): Promise<NavigationHeadingListResponse> {
  const params = new URLSearchParams({ limit: "100" });
  if (after) params.set("after", after);
  const response = await fetchNavigation(
    fetchWithAuth,
    `/api/sessions/${encodeURIComponent(sessionId)}/navigation/turns/${encodeURIComponent(turnId)}/headings?${params}`,
    signal,
  );
  if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status });
  if (!isJson(response)) throw new Error("navigation_headings_invalid_content_type");
  const payload: unknown = await response.json().catch(() => null);
  if (!isHeadingList(payload)) throw new Error("navigation_headings_invalid_response");
  return payload;
}
